import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBuildingNameClass, getSsDueTextClassFromDays } from '../utils/ssDueColor';
import { formatDueDateLocal } from '../utils/dueDate';
import { useDueQueueData } from '../hooks/useDueQueueData';
import { X, Building2, Calendar, AlertCircle, CheckCircle, Award } from 'lucide-react';
import { format } from 'date-fns';
import {
  getCachedTodoDismissedDate,
  saveTodoDismissedDate,
} from '../utils/userPreferences';

/**
 * Login To-Do popup ?? same Due queue as Dashboard (useDueQueueData).
 */
function TodoListModal({ onClose, settings }) {
  const navigate = useNavigate();
  const [dismissedToday, setDismissedToday] = useState(false);
  const { summary } = useDueQueueData();
  const overdueRows = summary.overdueRows;
  const urgentRows = summary.urgentRows;

  useEffect(() => {
    const dismissedDate = getCachedTodoDismissedDate();
    const todayStr = new Date().toDateString();
    if (dismissedDate === todayStr) setDismissedToday(true);
  }, []);

  const handleDismiss = () => {
    const today = new Date().toDateString();
    saveTodoDismissedDate(today);
    setDismissedToday(true);
    onClose();
  };

  if (dismissedToday && !settings?.showTodoList) {
    return null;
  }

  const daysLabel = (days) => {
    if (days == null) return '';
    if (days < 0) return `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
    if (days === 0) return 'Due today';
    return `${days} day${days === 1 ? '' : 's'} left`;
  };

  const openRow = (row) => {
    onClose();
    if (row.job_id) navigate(`/jobs/${row.job_id}`);
    else if (row.building_id) navigate(`/jobs/building/${row.building_id}`);
  };

  const renderRow = (row) => (
    <button
      key={row.key}
      type="button"
      onClick={() => openRow(row)}
      className={`w-full text-left border rounded-lg p-4 hover:shadow-sm transition-shadow ${
        row.bucket === 'overdue'
          ? 'border-red-300 bg-red-50'
          : 'border-orange-200 bg-orange-50'
      }`}
    >
      <div className="flex items-start gap-2">
        <Award className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-semibold ${getBuildingNameClass(row.days, row.application_approved)}`}>
              {row.building_name}
            </span>
            <span className={`text-sm font-bold ${getSsDueTextClassFromDays(row.days)}`}>
              {row.ss_code}
            </span>
            {row.ss_name && row.ss_name !== row.ss_code ? (
              <span className="text-sm text-gray-600">{row.ss_name}</span>
            ) : null}
          </div>
          <div className="mt-1 text-sm text-gray-700 flex flex-wrap gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Due: {formatDueDateLocal(row.due_date)} ({daysLabel(row.days)})
            </span>
            {row.building_address ? (
              <span className="inline-flex items-center gap-1 text-gray-600">
                <Building2 className="w-3.5 h-3.5" />
                {row.building_address}
              </span>
            ) : null}
            <span className="text-gray-500">
              {row.job_id ? `Inspection #${row.job_id}` : 'No inspection yet'}
            </span>
          </div>
        </div>
      </div>
    </button>
  );

  const empty =
    summary.overdueSs === 0 && summary.urgentSs === 0 && summary.upcomingSs === 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Today&apos;s To-Do List</h2>
            <p className="text-sm text-gray-500 mt-1">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Same Due queue as Dashboard: Over Due = past due · Urgent = still due ??0d
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800">Over Due</p>
              <p className="text-3xl font-bold text-red-950 mt-1 tabular-nums">{summary.overdueSs}</p>
              <p className="text-xs font-medium text-red-800/80 mt-0.5">SS (inspections)</p>
              <p className="text-xs text-red-700/70 mt-1">
                across {summary.overdueBuildings} building
                {summary.overdueBuildings === 1 ? '' : 's'} · past due
              </p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm font-medium text-orange-800">Urgent</p>
              <p className="text-3xl font-bold text-orange-950 mt-1 tabular-nums">{summary.urgentSs}</p>
              <p className="text-xs font-medium text-orange-800/80 mt-0.5">SS (inspections)</p>
              <p className="text-xs text-orange-700/70 mt-1">
                across {summary.urgentBuildings} building
                {summary.urgentBuildings === 1 ? '' : 's'} · still due ??0d
              </p>
            </div>
          </div>

          {overdueRows.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-red-700" />
                Over Due ({overdueRows.length} SS)
              </h3>
              <div className="space-y-2">{overdueRows.map(renderRow)}</div>
            </div>
          )}

          {urgentRows.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
                Urgent ??still due ??0d ({urgentRows.length} SS)
              </h3>
              <div className="space-y-2">{urgentRows.map(renderRow)}</div>
            </div>
          )}

          {empty && (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium text-gray-900 mb-2">All caught up!</p>
              <p className="text-sm text-gray-500">No overdue or urgent SS items right now.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={handleDismiss}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Don&apos;t show today
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/');
              }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white"
            >
              Open Dashboard
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodoListModal;
